import View                         from '@widesk-ui/components/View';
import Button                       from '@widesk-ui/components/Button';
import Input                        from '@widesk-ui/components/form/Input';
import Segmented                    from '@widesk-ui/Segmented';
import Select                       from '@widesk-ui/components/form/Select';
import React                        from 'react';
import { observer }                 from 'mobx-react-lite';
import { CopyOutlined }             from '@ant-design/icons';
import { DeleteOutlined }           from '@ant-design/icons';
import { LeftOutlined }             from '@ant-design/icons';
import { RightOutlined }            from '@ant-design/icons';
import { RotateLeftOutlined }       from '@ant-design/icons';
import { RotateRightOutlined }      from '@ant-design/icons';
import { SearchOutlined }           from '@ant-design/icons';
import { DECOR_CATEGORIES }         from '@tokamap/core';
import { DECOR_CATEGORIES_BY_ID }   from '@tokamap/core';
import { DECOR_MODELS }             from '@tokamap/core';
import { DECOR_MODELS_BY_ID }       from '@tokamap/core';
import { decorPathLength }          from '@tokamap/core';
import { defaultStairsDepth }       from '@tokamap/core';
import { defaultFlightRise }        from '@tokamap/core';
import { isFreeFlight }             from '@tokamap/core';
import { DEFAULT_DECOR_SMOKE }      from '@tokamap/core';
import { DEFAULT_STAIRS_SLOPE_DEG } from '@tokamap/core';
import { MAX_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import { MIN_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import { flightHasSlope }           from '@tokamap/core';
import { isDecorModelMovable }      from '@tokamap/core';
import { isFixedProceduralModel }   from '@tokamap/core';
import type { DecorItem }           from '@tokamap/core';
import type { DecorCategoryId }     from '@tokamap/core';
import type { DecorModel }          from '@tokamap/core';
import type { DecorPathEnd }        from '@tokamap/core';
import leftSidebarStore             from '@/TokamapEditor/stores/leftSidebarStore';
import currentEditorStore           from '@/TokamapEditor/stores/currentEditorStore';
import Field                        from '@/TokamapEditor/ux/AreaSidebar/Field';
import SliderInputNumber            from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import ImageUrlInput                from '@/TokamapEditor/ux/ImageUrlInput';
import useDesignTokens              from '@/hooks/useDesignTokens';
import decorStore                   from '@/TokamapEditor/decor/decorStore';
import DecorColorFields             from '@/TokamapEditor/decor/DecorColorFields';
import { decorSize }                from '@/TokamapEditor/decor/decorGeometry';
import { MAX_DECOR_SCALE }          from '@/TokamapEditor/decor/decorGeometry';
import { MIN_DECOR_SCALE }          from '@/TokamapEditor/decor/decorGeometry';

const PATH_ENDS: { value: DecorPathEnd; label: string }[] = [
	{ value: 'restart', label: 'Revient au départ' },
	{ value: 'pingpong', label: 'Fait demi-tour' },
	{ value: 'loop', label: 'Boucle fermée' },
	{ value: 'vanish', label: 'Disparaît au bout, puis revient au départ' },
];

const MOVING_SUBJECT: Partial<Record<DecorCategoryId, string>> = {
	vehicle: 'le véhicule',
	person: 'la personne',
	animal: "l'animal",
};

const proceduralDefault = (model: DecorModel, key: 'width' | 'length') => (model.size as Partial<Record<'width' | 'length', number>>)[key] ?? 0;

const formatMeters = (value: number) => `${value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m`;

const DRAG_THRESHOLD_PX = 5;

// Glisser à la souris plutôt que le glisser natif du navigateur, qui n'émet aucune molette : Maj + molette
// doit pouvoir pivoter l'objet en cours de route.
const pressModel = (event: React.MouseEvent, model: DecorModel) => {
	if (event.button !== 0) return;
	event.preventDefault();
	const start = { x: event.clientX, y: event.clientY };

	const onMove = (move: MouseEvent) => {
		if (decorStore.dragging || Math.hypot(move.clientX - start.x, move.clientY - start.y) < DRAG_THRESHOLD_PX) return;
		decorStore.startDrag(model.id);
	};
	const onUp = () => {
		window.removeEventListener('mousemove', onMove);
		window.removeEventListener('mouseup', onUp);
		decorStore.endDrag();
	};

	window.addEventListener('mousemove', onMove);
	window.addEventListener('mouseup', onUp);
};

const ModelCard = ({ model, active, onClick, compact, draggable }: { model: DecorModel; active: boolean; onClick: () => void; compact?: boolean; draggable?: boolean; }) => {
	const tokens = useDesignTokens();

	return (
		<div
			onMouseDown={draggable ? event => pressModel(event, model) : undefined}
			style={{ display: 'grid', flexShrink: compact ? 0 : undefined }}
		>
			<View
				onClick={onClick}
				pointer
				center
				gap={2}
				p={6}
				br={8}
				size={12}
				color={active ? tokens.color.primary : tokens.text.default}
				bg={active ? `color-mix(in srgb, ${tokens.color.primary} 12%, transparent)` : tokens.surface.card}
				hover={active ? undefined : { bg: tokens.surface.subtle }}
				style={{
					border: `1px solid ${active ? tokens.color.primary : tokens.border.subtle}`,
					textAlign: 'center',
					width: compact ? 72 : undefined,
				}}
			>
				<img src={model.thumbnailUrl} alt="" width={compact ? 56 : 72} height={compact ? 43 : 56} style={{ objectFit: 'contain' }} />
				{model.label}
			</View>
		</div>
	);
};

const CAROUSEL_CARD_WIDTH = 72;
const CAROUSEL_GAP = 6;
const CAROUSEL_STEP_CARDS = 3;

const ModelCarousel = ({ models, activeId, onPick }: { models: DecorModel[]; activeId: string; onPick: (modelId: string) => void; }) => {
	const track = React.useRef<HTMLDivElement>(null);
	const [edges, setEdges] = React.useState({ start: true, end: true });
	const pitch = CAROUSEL_CARD_WIDTH + CAROUSEL_GAP;

	const measure = React.useCallback(() => {
		const element = track.current;
		if (!element) return;
		setEdges({
			start: element.scrollLeft <= 1,
			end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
		});
	}, []);

	React.useEffect(() => {
		const element = track.current;
		if (!element) return;
		const index = models.findIndex(candidate => candidate.id === activeId);
		if (index >= 0) element.scrollLeft = index * pitch - (element.clientWidth - CAROUSEL_CARD_WIDTH) / 2;
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [models.length]);

	const scroll = (direction: 1 | -1) => {
		track.current?.scrollBy({ left: direction * pitch * CAROUSEL_STEP_CARDS });
		measure();
	};
	const overflowing = !(edges.start && edges.end);

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
			{overflowing && <Button size="small" icon={<LeftOutlined />} disabled={edges.start} onClick={() => scroll(-1)} />}
			<div
				ref={track}
				onScroll={measure}
				style={{ display: 'flex', gap: CAROUSEL_GAP, overflowX: 'auto', flex: 1, minWidth: 0, scrollbarWidth: 'none', paddingBottom: 2 }}
			>
				{models.map(candidate => (
					<ModelCard
						key={candidate.id}
						compact
						model={candidate}
						active={candidate.id === activeId}
						onClick={() => onPick(candidate.id)}
					/>
				))}
			</div>
			{overflowing && <Button size="small" icon={<RightOutlined />} disabled={edges.end} onClick={() => scroll(1)} />}
		</div>
	);
};

export const DecorCatalogue = observer(() => {
	const tokens = useDesignTokens();
	const [search, setSearch] = React.useState('');
	const query = search.trim().toLowerCase();

	const groups = DECOR_CATEGORIES
		.map(category => ({
			category,
			models: DECOR_MODELS.filter(model => model.category === category.id
				&& (!query || model.label.toLowerCase().includes(query) || category.label.toLowerCase().includes(query))),
		}))
		.filter(group => group.models.length > 0);

	return (
		<View>
			<View p="md" bg={tokens.surface.card} style={{ position: 'sticky', top: 0, zIndex: 1 }}>
				<Input
					allowClear
					prefix={<SearchOutlined style={{ color: tokens.text.muted }} />}
					placeholder="Voiture, arbre, banc…"
					value={search}
					onChange={(e: unknown) => setSearch((e as React.ChangeEvent<HTMLInputElement>).target?.value ?? String(e ?? ''))}
				/>
			</View>

			<View px="md" pb="md" gap="lg">
				{groups.map(({ category, models }) => (
					<View key={category.id} gap="xs">
						<View size={12} bold color={tokens.text.secondary} style={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>
							{category.label}
						</View>

						<View style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
							{models.map(model => (
								<ModelCard
									key={model.id}
									model={model}
									active={decorStore.armedModelId === model.id}
									onClick={() => decorStore.arm(model.id)}
									draggable
								/>
							))}
						</View>
					</View>
				))}

				{!groups.length && <View color={tokens.text.secondary}>Aucun élément ne correspond.</View>}
			</View>
		</View>
	);
});

export const DecorItemTitle = observer(() => {
	const item = decorStore.selected;

	return <>{item ? DECOR_MODELS_BY_ID[item.model]?.label ?? 'Élément' : 'Élément'}</>;
});

export const DecorItemSubtitle = observer(() => {
	const item = decorStore.selected;
	const model = item && DECOR_MODELS_BY_ID[item.model];

	return <>{model ? DECOR_CATEGORIES_BY_ID[model.category].label : ''}</>;
});

export const DecorItemActions = observer(() => {
	const item = decorStore.selected;
	if (!item) return null;

	return (
		<View row gap="sm">
			<Button icon={<CopyOutlined />} onClick={() => decorStore.duplicate(item.id)}>Dupliquer</Button>
			<Button danger icon={<DeleteOutlined />} onClick={() => decorStore.remove(item.id)}>Supprimer</Button>
		</View>
	);
});

export const DecorItemPanel = observer(() => {
	const item = decorStore.selected;

	React.useEffect(() => {
		if (!item && leftSidebarStore.options.reference === 'decor-item') leftSidebarStore.close();
	}, [item]);

	if (!item) return null;

	return <DecorItemFields item={item} />;
});

const DecorItemFields = observer(({ item }: { item: DecorItem; }) => {
	const tokens = useDesignTokens();
	const model = DECOR_MODELS_BY_ID[item.model];
	void decorStore.loadedModels;
	const bounds = currentEditorStore.get?.mapAdapter.getDecorItemBounds(item);
	const size = bounds && decorSize(item, bounds);
	const category = model && DECOR_CATEGORIES_BY_ID[model.category];
	const freeStanding = !!model && isFreeFlight(model);
	const stage = model?.procedural === 'stage' || model?.procedural === 'seating' || model?.procedural === 'picnic';
	const massif = model?.procedural === 'rock-massif';

	return (
		<View p="md" gap="lg">
			<Field label="Modèle">
				<ModelCarousel
					models={DECOR_MODELS.filter(candidate => candidate.category === model?.category)}
					activeId={item.model}
					onPick={modelId => decorStore.update(item.id, { model: modelId })}
				/>
			</Field>

			<DecorColorFields item={item} />

			{model?.procedural && !isFixedProceduralModel(model) ? (
				<>
					<Field label="Largeur">
						<SliderInputNumber
							min={freeStanding ? 1 : 2}
							max={freeStanding ? 10 : stage || massif ? 120 : 40}
							step={freeStanding ? 0.1 : 0.5}
							suffix="m"
							value={item.width ?? proceduralDefault(model, 'width')}
							onChange={value => value !== undefined && decorStore.update(item.id, { width: value }, true)}
							onAfterChange={value => value !== undefined && decorStore.update(item.id, { width: value })}
						/>
					</Field>

					{model && flightHasSlope(model) && (
						<Field label="Pente" help="Plus raide, l'escalier est plus court pour le même dénivelé : ses marches sont moins profondes.">
							<SliderInputNumber
								min={MIN_STAIRS_SLOPE_DEG}
								max={MAX_STAIRS_SLOPE_DEG}
								step={1}
								resetValue={Math.round(DEFAULT_STAIRS_SLOPE_DEG)}
								suffix="°"
								value={Math.round(item.slopeDeg ?? DEFAULT_STAIRS_SLOPE_DEG)}
								onChange={value => value !== undefined && decorStore.update(item.id, { slopeDeg: value }, true)}
								onAfterChange={value => value !== undefined && decorStore.update(item.id, { slopeDeg: value })}
							/>
						</Field>
					)}

					{!freeStanding && (
						<Field label={stage ? 'Profondeur' : 'Longueur'}>
							<SliderInputNumber
								min={3}
								max={stage ? 60 : massif ? 120 : 40}
								step={0.5}
								suffix="m"
								value={item.length ?? proceduralDefault(model, 'length')}
								onChange={value => value !== undefined && decorStore.update(item.id, { length: value }, true)}
								onAfterChange={value => value !== undefined && decorStore.update(item.id, { length: value })}
							/>
						</Field>
					)}

					{model.procedural === 'stage' && (
						<Field label="Écran" help="Image affichée sur l'écran du fond de la scène, sans déformation.">
							<ImageUrlInput
								value={item.image}
								onChange={value => decorStore.update(item.id, { image: value })}
							/>
						</Field>
					)}

					{massif && (
						<Field label="Hauteur" help="Hauteur du massif à son point le plus haut.">
							<SliderInputNumber
								min={1}
								max={80}
								step={0.5}
								suffix="m"
								value={item.height ?? model.defaultHeight ?? 10}
								onChange={value => value !== undefined && decorStore.update(item.id, { height: value }, true)}
								onAfterChange={value => value !== undefined && decorStore.update(item.id, { height: value })}
							/>
						</Field>
					)}

					{!stage && !massif && (
						<Field
							label="Dénivelé"
							help={freeStanding
								? 'Hauteur entre le bas, posé au sol, et le palier du haut. Sa longueur en découle.'
								: 'Hauteur descendue jusqu\'à la dernière marche. Pour un escalier qui débouche sur une zone creusée, la profondeur de cette zone.'}
						>
							<SliderInputNumber
								min={freeStanding ? 0.3 : 1}
								max={freeStanding ? 40 : 20}
								step={0.1}
								suffix="m"
								value={Math.round((item.depth ?? (freeStanding ? defaultFlightRise(model) : defaultStairsDepth(item.length ?? proceduralDefault(model, 'length')))) * 10) / 10}
								onChange={value => value !== undefined && decorStore.update(item.id, { depth: value }, true)}
								onAfterChange={value => value !== undefined && decorStore.update(item.id, { depth: value })}
							/>
						</Field>
					)}

					{!freeStanding && !stage && !massif && (
						<Field
							label="En bas"
							help="Fermé : l'escalier s'enfonce sous le sol, comme une bouche de métro. Ouvert : il débouche à l'air libre sur une zone creusée."
						>
							<Segmented
								block
								size="small"
								value={item.openBottom ? 'open' : 'closed'}
								onChange={value => decorStore.update(item.id, { openBottom: value === 'open' || undefined })}
								options={[
									{ value: 'closed', label: 'Fermé' },
									{ value: 'open', label: 'Ouvert' },
								]}
							/>
						</Field>
					)}
				</>
			) : (
				<Field label="Taille">
					<SliderInputNumber
						min={MIN_DECOR_SCALE}
						max={MAX_DECOR_SCALE}
						step={0.05}
						resetValue={1}
						suffix="×"
						value={item.scale}
						onChange={value => value !== undefined && decorStore.update(item.id, { scale: value }, true)}
						onAfterChange={value => value !== undefined && decorStore.update(item.id, { scale: value })}
					/>

					{size && (
						<View row gap="xs" size={12} color={tokens.text.secondary}>
							{formatMeters(size.length)} de long · {formatMeters(size.width)} de large · {formatMeters(size.height)} de haut
						</View>
					)}
				</Field>
			)}

			<Field label="Orientation">
				<SliderInputNumber
					min={0}
					max={359}
					step={1}
					suffix="°"
					value={item.rotationDeg}
					onChange={value => value !== undefined && decorStore.update(item.id, { rotationDeg: value }, true)}
					onAfterChange={value => value !== undefined && decorStore.update(item.id, { rotationDeg: value })}
				/>

				<View row gap="xs">
					<Button size="small" icon={<RotateLeftOutlined />} onClick={() => decorStore.update(item.id, { rotationDeg: item.rotationDeg - 90 })}>90°</Button>
					<Button size="small" icon={<RotateRightOutlined />} onClick={() => decorStore.update(item.id, { rotationDeg: item.rotationDeg + 90 })}>90°</Button>
				</View>
			</Field>

			{model?.smokes && (
				<Field label="Fumée" help="Hauteur jusqu'où monte la fumée de la cigarette. À 0 %, il n'y en a plus.">
					<SliderInputNumber
						min={0}
						max={100}
						step={5}
						resetValue={DEFAULT_DECOR_SMOKE * 100}
						suffix="%"
						value={Math.round((item.smoke ?? DEFAULT_DECOR_SMOKE) * 100)}
						onChange={value => value !== undefined && decorStore.update(item.id, { smoke: value / 100 }, true)}
						onAfterChange={value => value !== undefined && decorStore.update(item.id, { smoke: value / 100 })}
					/>
				</Field>
			)}

			{model?.reversible && (
				<Field label="Marches">
					<Segmented
						block
						size="small"
						value={item.reverse ? 'reverse' : 'forward'}
						onChange={value => decorStore.update(item.id, { reverse: value === 'reverse' || undefined })}
						options={[
							{ value: 'forward', label: model.id.endsWith('-down') ? 'Descendent' : 'Montent' },
							{ value: 'reverse', label: model.id.endsWith('-down') ? 'Montent' : 'Descendent' },
						]}
					/>
				</Field>
			)}

			<Field
				label="Hauteur"
				help="Par défaut, l'élément se pose sur la dalle ou le trottoir qui se trouve sous lui."
			>
				{item.elevation === undefined ? (
					<View row gap="xs" align="center" size={12} color={tokens.text.secondary}>
						Posé automatiquement sur le sol.
						<Button size="small" onClick={() => decorStore.update(item.id, { elevation: 0 })}>Forcer une hauteur</Button>
					</View>
				) : (
					<View gap="xs">
						<SliderInputNumber
							min={-2}
							max={20}
							step={0.05}
							resetValue={0}
							suffix="m"
							value={item.elevation}
							onChange={value => value !== undefined && decorStore.update(item.id, { elevation: value }, true)}
							onAfterChange={value => value !== undefined && decorStore.update(item.id, { elevation: value })}
						/>
						<View row>
							<Button size="small" onClick={() => decorStore.update(item.id, { elevation: undefined })}>Revenir à l'automatique</Button>
						</View>
					</View>
				)}
			</Field>

			{category && model && isDecorModelMovable(model) && <DecorAnimationFields item={item} defaultSpeedKmh={category.defaultSpeedKmh} />}
		</View>
	);
});

const DecorAnimationFields = observer(({ item, defaultSpeedKmh }: { item: DecorItem; defaultSpeedKmh: number; }) => {
	const tokens = useDesignTokens();
	const drawing = decorStore.pathDraft?.itemId === item.id;
	const hasPath = !!item.path && item.path.length > 1;
	const speed = item.speedKmh ?? defaultSpeedKmh;
	const category = DECOR_MODELS_BY_ID[item.model]?.category;
	const vehicle = category === 'vehicle';

	return (
		<Field
			label="Déplacement"
			help="L'élément parcourt son trajet en boucle sur la carte publiée et dans l'aperçu. Il reste immobile pendant l'édition."
		>
			{drawing ? (
				<View size={12} color={tokens.text.secondary}>
					Cliquez sur la carte pour ajouter des points, double-cliquez pour terminer.
				</View>
			) : hasPath ? (
				<View gap="xs">
					<View size={12} color={tokens.text.secondary}>
						Trajet de {formatMeters(decorPathLength(item))} en {item.path!.length} points.
					</View>
					<View row gap="xs">
						<Button size="small" onClick={() => decorStore.startPath(item.id)}>Retracer le trajet</Button>
						<Button size="small" danger onClick={() => decorStore.removePath(item.id)}>Retirer</Button>
					</View>
				</View>
			) : (
				<View gap="xs">
					<View size={12} color={tokens.text.secondary}>
						Tracez la ligne que {(category && MOVING_SUBJECT[category]) ?? "l'élément"} va suivre, depuis sa position.
					</View>
					<View row>
						<Button size="small" type="primary" onClick={() => decorStore.startPath(item.id)}>Tracer le trajet</Button>
					</View>
				</View>
			)}

			{hasPath && !drawing && (
				<View gap="sm">
					<SliderInputNumber
						min={1}
						max={vehicle ? 90 : 15}
						step={0.5}
						suffix="km/h"
						value={speed}
						onChange={value => value !== undefined && decorStore.update(item.id, { speedKmh: value }, true)}
						onAfterChange={value => value !== undefined && decorStore.update(item.id, { speedKmh: value })}
					/>

					<Select
						value={item.pathEnd ?? 'restart'}
						onChange={value => decorStore.update(item.id, { pathEnd: value as DecorPathEnd })}
						options={PATH_ENDS}
					/>
				</View>
			)}
		</Field>
	);
});
